import React, { useEffect, useState } from 'react';
import { MediaService } from '../../services/mediaService';
import Modal from '../Modal/Modal';
import styles from './SeasonsList.module.css';
import { MediaCollectorStats } from '../CollectorStats/CollectorStats';

const RARITY_LABELS = { bronze: '🥉 Bronze', silver: '🥈 Silver', gold: '🥇 Gold' };
const RARITY_COLORS = {
    gold:   { bg: 'rgba(255,215,0,0.15)',  border: '#FFD700', text: '#B8860B' },
    silver: { bg: 'rgba(192,192,192,0.15)', border: '#A8A8A8', text: '#707070' },
    bronze: { bg: 'rgba(205,127,50,0.15)',  border: '#CD7F32', text: '#8B4513' },
};

/**
 * Props:
 *   tmdbId         {number}     — TMDB id of the show
 *   onVerify       {function}   — called with (seasonNumber)
 *   earnedSeasons  {number[]}   — season numbers the user has already verified
 *   seasonProgress {object[]}   — WatchlistTvSeasonStatus[] from watchlist
 */
export default function SeasonsList({ tmdbId, onVerify, onAddSeasonToList = null, earnedSeasons = [], seasonProgress = [] }) {
    const [seasons, setSeasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSeason, setSelectedSeason] = useState(null);
    const [seasonDetails, setSeasonDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [loadingQuiz, setLoadingQuiz] = useState(false);

    useEffect(() => {
        if (!tmdbId) return;
        MediaService.getTvSeasons(tmdbId)
            .then((d) => setSeasons(d ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [tmdbId]);

    const getWatchStatus = (seasonNumber) => {
        const progress = seasonProgress.find((p) => p.seasonNumber === seasonNumber);
        if (earnedSeasons.includes(seasonNumber)) return 'verified';
        if (progress?.status === 'watched') return 'watched';
        if (progress?.status === 'saved') return 'saved';
        return 'none';
    };

    const handleSeasonClick = async (season) => {
        setSelectedSeason(season);
        setLoadingDetails(true);
        try {
            const data = await MediaService.getTvSeasonEpisodes(tmdbId, season.seasonNumber);
            setSeasonDetails(data);
        } catch (err) {
            console.error('Failed to load season details:', err);
            setSeasonDetails(null);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleCloseModal = () => {
        setSelectedSeason(null);
        setSeasonDetails(null);
        setLoadingQuiz(false);
    };

    const handleVerifyClick = async (seasonNumber) => {
        setLoadingQuiz(true);
        try {
            await onVerify(seasonNumber);
        } catch (error) {
            console.error('Failed to start quiz:', error);
            setLoadingQuiz(false);
        }
        // Note: Modal will stay open until navigation happens
    };

    if (loading) return <div className={styles.loader}>Loading seasons…</div>;

    return (
        <>
            <div className={styles.list}>
                {seasons.map((season) => {
                    const watchStatus = getWatchStatus(season.seasonNumber);
                    const isVerified = watchStatus === 'verified';
                    const canVerify = season.canVerify ?? !isVerified;
                    const lockReason = season.verificationLockReason ?? null;
                    const isAiring = lockReason === 'season_airing';
                    const isComingSoon = lockReason === 'coming_soon';
                    const coin = season.userCoin ?? null;
                    const hasCoin = !!coin;
                    const canUpgrade = hasCoin && coin.rarity !== 'gold' && coin.canRetry;
                    const rc = coin ? RARITY_COLORS[coin.rarity] : null;

                    return (
                        <div
                            key={season.seasonNumber}
                            className={styles.card}
                            onClick={() => handleSeasonClick(season)}
                        >
                            {season.posterUrl ? (
                                <img src={season.posterUrl} alt={season.name} className={styles.poster} />
                            ) : (
                                <div className={styles.posterPlaceholder} />
                            )}

                            <div className={styles.info}>
                                <div className={styles.nameRow}>
                                    <span className={styles.name}>{season.name}</span>
                                    {hasCoin && (
                                        <span
                                            className={styles.coinPill}
                                            style={{
                                                backgroundColor: rc.bg,
                                                borderColor: rc.border,
                                                color: rc.text
                                            }}
                                        >
                                            {RARITY_LABELS[coin.rarity]}
                                        </span>
                                    )}
                                </div>

                                {season.episodeCount != null && (
                                    <span className={styles.meta}>{season.episodeCount} episodes</span>
                                )}

                                {/* Brief overview */}
                                {season.overview && (
                                    <p className={styles.briefOverview}>{season.overview}</p>
                                )}

                                {/* Upgrade eligible */}
                                {canUpgrade && (
                                    <span className={`${styles.statusLabel} ${styles.upgrade}`}>
                                        ✨ Upgrade available!
                                    </span>
                                )}

                                {/* Availability badge */}
                                {!isVerified && isAiring && (
                                    <span className={`${styles.lockLabel} ${styles.airing}`}>📺 Currently Airing</span>
                                )}
                                {!isVerified && isComingSoon && (
                                    <span className={styles.lockLabel}>🗓 Coming Soon</span>
                                )}

                                <span className={styles.tapHint}>Click for details ›</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Season Detail Modal */}
            <Modal isOpen={!!selectedSeason} onClose={handleCloseModal}>
                {selectedSeason && (
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalHeaderInfo}>
                                {selectedSeason.posterUrl && (
                                    <img
                                        src={selectedSeason.posterUrl}
                                        alt={selectedSeason.name}
                                        className={styles.modalPoster}
                                    />
                                )}
                                <div>
                                    <h2 className={styles.modalTitle}>{selectedSeason.name}</h2>
                                    {selectedSeason.episodeCount != null && (
                                        <p className={styles.modalMeta}>{selectedSeason.episodeCount} episodes</p>
                                    )}
                                    {selectedSeason.airDate && (
                                        <p className={styles.modalMeta}>{selectedSeason.airDate.slice(0, 4)}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {loadingDetails ? (
                            <div className={styles.detailsLoader}>Loading details...</div>
                        ) : seasonDetails ? (
                            <>
                                {/* Season Overview */}
                                {seasonDetails.overview && (
                                    <div className={styles.overviewSection}>
                                        <h3>Season Overview</h3>
                                        <p>{seasonDetails.overview}</p>
                                    </div>
                                )}

                                {/* Coin Info */}
                                {seasonDetails.userCoin && (
                                    <div className={styles.coinSection}>
                                        <h3>Your Coin</h3>
                                        <div
                                            className={styles.coinCard}
                                            style={{
                                                backgroundColor: RARITY_COLORS[seasonDetails.userCoin.rarity].bg,
                                                borderColor: RARITY_COLORS[seasonDetails.userCoin.rarity].border,
                                            }}
                                        >
                                            <div className={styles.coinCardHeader}>
                                                <span className={styles.coinIcon}>
                                                    {RARITY_LABELS[seasonDetails.userCoin.rarity].split(' ')[0]}
                                                </span>
                                                <div>
                                                    <strong style={{ color: RARITY_COLORS[seasonDetails.userCoin.rarity].text }}>
                                                        {RARITY_LABELS[seasonDetails.userCoin.rarity]} Coin Earned
                                                    </strong>
                                                    <p className={styles.coinDate}>
                                                        Earned on {new Date(seasonDetails.userCoin.earnedAt || seasonDetails.userCoin.watchedAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>

                                            {seasonDetails.userCoin.coinImageUrl && (
                                                <img
                                                    src={seasonDetails.userCoin.coinImageUrl}
                                                    alt={`${seasonDetails.userCoin.rarity} coin`}
                                                    className={styles.coinImage}
                                                />
                                            )}

                                            {/* Upgrade Status */}
                                            {seasonDetails.userCoin.rarity === 'gold' ? (
                                                <div className={styles.upgradeStatus}>
                                                    ⭐ You have the highest rarity!
                                                </div>
                                            ) : seasonDetails.userCoin.canRetry ? (
                                                <div className={`${styles.upgradeStatus} ${styles.canUpgrade}`}>
                                                    ✨ You can upgrade your {seasonDetails.userCoin.rarity} coin by taking another challenge!
                                                </div>
                                            ) : (
                                                <div className={styles.upgradeStatus}>
                                                    ⏱ Upgrade on cooldown
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* No Coin Yet */}
                                {!seasonDetails.userCoin && selectedSeason.canVerify && (
                                    <div className={styles.noCoinSection}>
                                        <span className={styles.noCoinEmoji}>🪙</span>
                                        <h3>No coin yet</h3>
                                        <p>Pass the challenge for this season to earn your coin.</p>
                                    </div>
                                )}

                                {/* Aggregate collector counts for this season's coin */}
                                <MediaCollectorStats
                                    stats={seasonDetails.collectorStats ?? selectedSeason.collectorStats}
                                    className={styles.collectorStats}
                                />

                                {/* Verify Button */}
                                {selectedSeason.canVerify && (
                                    <button
                                        className={styles.verifyBtnModal}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleVerifyClick(selectedSeason.seasonNumber);
                                        }}
                                        disabled={loadingQuiz}
                                    >
                                        {loadingQuiz
                                            ? '🔄 Preparing your challenge...'
                                            : seasonDetails.userCoin?.canRetry
                                                ? '✨ Retry to Upgrade Coin'
                                                : seasonDetails.userCoin
                                                    ? '✅ Already Verified'
                                                    : '🪙 Verify This Season'}
                                    </button>
                                )}

                                {/* Season-specific "Add to List" (My Lists are season-aware) */}
                                {onAddSeasonToList && (
                                    <button
                                        type="button"
                                        className={styles.addToListBtnModal}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAddSeasonToList(selectedSeason.seasonNumber);
                                        }}
                                    >
                                        ☰ Add Season {selectedSeason.seasonNumber} to List
                                    </button>
                                )}

                                {/* Locked Message */}
                                {!selectedSeason.canVerify && (
                                    <div className={styles.lockedMessage}>
                                        {selectedSeason.verificationLockReason === 'season_airing'
                                            ? '📺 Challenge unlocks after the season finale airs'
                                            : selectedSeason.verificationLockReason === 'season_quiz_coming_soon'
                                                ? (selectedSeason.quizAvailability?.availableAtFormatted
                                                    ? `⏳ Challenge unlocks ${selectedSeason.quizAvailability.availableAtFormatted}`
                                                    : '⏳ Challenge coming soon')
                                            : selectedSeason.airDate
                                                ? `🗓 Premieres ${new Date(selectedSeason.airDate).toLocaleDateString()}`
                                                : '🗓 Not available yet'}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={styles.detailsError}>Failed to load season details</div>
                        )}
                    </div>
                )}
            </Modal>
        </>
    );
}